import type { ApiClient, TokenProvider } from './client';
import { ApiError, messageForCode } from './errors';
import type {
  AgentPersonaCreate,
  AgentPersonaPut,
  AgentsListing,
  AgentsResponse,
  AiModelSettings,
  AiModelSettingsPatch,
  AppUser,
  AssignableProfile,
  AskAsyncInput,
  AskAccepted,
  AskInput,
  AskJob,
  AskResult,
  AskStreamEvent,
  AskSurface,
  BillingCheckoutInput,
  BillingPaymentsResponse,
  BillingPlansResponse,
  BillingReferrals,
  BillingSubscription,
  Citation,
  Conversation,
  ConversationDetail,
  ConversationListParams,
  ConversationPage,
  ConversationPatch,
  CurrentUser,
  DashboardSummary,
  Deck,
  DeckInput,
  DeckUpdateInput,
  Dependent,
  DependentCode,
  DependentCreate,
  DependentPatch,
  DependentReminder,
  DependentReminderCreate,
  DependentRemindersResponse,
  DependentsResponse,
  Document,
  DocumentVisibility,
  EnrichDocumentInput,
  ExamBoardsResponse,
  FeedbackInput,
  FeedbackResult,
  IngestionRun,
  IngestionRunsPage,
  KnowledgeArea,
  GeneratedImageAsset,
  MarketingPopup,
  MarketingPopupPatch,
  SiteContent,
  SiteContentResponse,
  SocialLibrary,
  SocialPost,
  SocialPostInput,
  SocialSettings,
  MetricsCost,
  MetricsOverview,
  ModerationItem,
  ModerationItemDetail,
  ModerationListParams,
  MyPersona,
  MyPersonaCreated,
  MyPersonasResponse,
  Page,
  PageParams,
  PersonaDocument,
  PersonaDocumentsParams,
  PersonaUserCreate,
  PersonaUserPut,
  PreferencesPatch,
  SignupContextInput,
  Profile,
  ProfileCreate,
  ProfileDeletionImpact,
  ProfilePatch,
  QAInteraction,
  ReindexInput,
  SiteChatInput,
  SiteChatReply,
  TaxonomyArea,
  TaxonomyAreaCreate,
  TaxonomyNode,
  TaxonomyNodePatch,
  TaxonomyResponse,
  TaxonomySubareaCreate,
  Training,
  TrainingInput,
  TranscriptionResult,
  UploadDocumentMeta,
  UploadDocumentResult,
  UserInput,
  UserPatch,
  UUID,
  WhatsAppGroup,
  WhatsAppGroupAgentCandidates,
  WhatsAppGroupAgentsChoice,
  WhatsAppGroupCode,
  WhatsAppGroups,
  WhatsAppLink,
  WhatsAppLinkStarted,
} from './types';

interface HttpClientOptions {
  baseUrl: string;
  getToken: TokenProvider;
}

type Query = Record<string, string | number | boolean | undefined>;

interface RequestOptions {
  query?: Query;
  body?: unknown;
  /** What to say when the server fails with a body this client cannot read. The
   *  generic fallback ("Erro inesperado.") is a lie on endpoints whose work may
   *  well have continued past the gateway's timeout. */
  serverErrorMessage?: string;
  /** A public route: the session token, if there is one, never rides along. */
  anonymous?: boolean;
}

// Files at or below this go through the API Gateway multipart endpoint; larger
// ones take the presigned-S3 path that bypasses the 10 MB GW cap. That path
// accepts up to 40 MB for any format — and up to 100 MB for a PDF, which the API
// compresses back under 40 MB. Both edges answer 202: the indexing itself never
// happens inside the request (see {@link UploadDocumentResult}).
const DIRECT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

// What a 5xx with no JSON body means on the upload path. The API Gateway's own
// 504 has no body and (over HTTP/2) no statusText either, which is how a document
// that was being indexed used to reach the console as "Erro inesperado.". Every
// upload is accepted asynchronously now, so this is residual defence — and it must
// still point at the tab that holds the truth instead of guessing at a failure.
const INGEST_SERVER_ERROR_MESSAGE =
  'O servidor demorou demais para responder; o processamento pode ter continuado — ' +
  'confira a aba Ingestão.';

/**
 * Real client against the habilita-api /v1 contract. Attaches a fresh bearer
 * token per request, serializes query params, and normalizes failures to
 * {@link ApiError}. Stateless beyond the injected token provider.
 */
export class HttpApiClient implements ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;

  constructor({ baseUrl, getToken }: HttpClientOptions) {
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    this.getToken = getToken;
  }

  // ---- Identity ------------------------------------------------------------

  getMe(): Promise<CurrentUser> {
    return this.request<CurrentUser>('GET', '/v1/me');
  }

  listExamBoards(profile?: string): Promise<ExamBoardsResponse> {
    return this.request('GET', '/v1/exam-boards', { query: { profile } });
  }

  /**
   * O corpo carrega EXATAMENTE as chaves que o chamador montou: `JSON.stringify`
   * descarta as ausentes, que é como o servidor distingue "limpar" (`null`
   * presente) de "não mexer" (chave ausente).
   */
  updateMyPreferences(patch: PreferencesPatch): Promise<CurrentUser> {
    return this.request('PATCH', '/v1/me/preferences', { body: patch });
  }

  uploadMyAvatar(file: File): Promise<CurrentUser> {
    const form = new FormData();
    form.append('file', file);
    return this.request('POST', '/v1/me/avatar', { body: form });
  }

  deleteMyAccount(): Promise<void> {
    return this.request('DELETE', '/v1/me');
  }

  acceptSignupContext(input: SignupContextInput): Promise<CurrentUser> {
    return this.request('POST', '/v1/me/signup-context', { body: input });
  }

  getPublicMarketingPopup(): Promise<MarketingPopup> {
    return this.request('GET', '/public/marketing-popup', { anonymous: true });
  }

  getMarketingPopup(): Promise<MarketingPopup> {
    return this.request('GET', '/v1/settings/marketing-popup');
  }

  updateMarketingPopup(patch: MarketingPopupPatch): Promise<MarketingPopup> {
    return this.request('PUT', '/v1/settings/marketing-popup', { body: patch });
  }

  uploadMarketingPopupImage(file: File): Promise<MarketingPopup> {
    const form = new FormData();
    form.append('file', file);
    return this.request('POST', '/v1/settings/marketing-popup/image', { body: form });
  }

  generateMarketingImage(input: { prompt: string; includeLogo: boolean }): Promise<GeneratedImageAsset> {
    return this.request('POST', '/v1/settings/marketing-popup/generate-image', { body: input });
  }

  getPublicSiteContent(): Promise<SiteContentResponse> {
    return this.request('GET', '/public/site-content', { anonymous: true });
  }

  getSiteContent(): Promise<SiteContentResponse> {
    return this.request('GET', '/v1/settings/site-content');
  }

  updateSiteContent(content: SiteContent): Promise<SiteContentResponse> {
    return this.request('PUT', '/v1/settings/site-content', { body: content });
  }

  getSocialLibrary(): Promise<SocialLibrary> {
    return this.request('GET', '/v1/settings/social');
  }

  createSocialPost(input: SocialPostInput): Promise<SocialPost> {
    return this.request('POST', '/v1/settings/social/posts', { body: input });
  }

  updateSocialSettings(settings: SocialSettings): Promise<SocialSettings> {
    return this.request('PUT', '/v1/settings/social/preferences', { body: settings });
  }

  // ---- Profiles ------------------------------------------------------------

  listProfiles(): Promise<{ profiles: Profile[] }> {
    return this.request('GET', '/v1/profiles');
  }

  createProfile(input: ProfileCreate): Promise<Profile> {
    return this.request('POST', '/v1/profiles', { body: input });
  }

  updateProfile(slug: string, patch: ProfilePatch): Promise<Profile> {
    return this.request('PATCH', `/v1/profiles/${encodeURIComponent(slug)}`, { body: patch });
  }

  getProfileDeletionImpact(slug: string): Promise<ProfileDeletionImpact> {
    return this.request('GET', `/v1/profiles/${encodeURIComponent(slug)}/deletion-impact`);
  }

  deleteProfile(slug: string, options: { dependentsTo?: string } = {}): Promise<void> {
    return this.request('DELETE', `/v1/profiles/${encodeURIComponent(slug)}`, {
      query: { dependentsTo: options.dependentsTo },
    });
  }

  listAssignableProfiles(to: 'user' | 'dependent'): Promise<{ profiles: AssignableProfile[] }> {
    return this.request('GET', '/v1/profiles/assignable', { query: { to } });
  }

  // ---- Dependentes ---------------------------------------------------------

  listMyDependents(): Promise<DependentsResponse> {
    return this.request('GET', '/v1/me/dependents');
  }

  createMyDependent(input: DependentCreate): Promise<Dependent> {
    return this.request('POST', '/v1/me/dependents', { body: input });
  }

  updateMyDependent(id: UUID, patch: DependentPatch): Promise<Dependent> {
    return this.request('PATCH', `/v1/me/dependents/${id}`, { body: patch });
  }

  deleteMyDependent(id: UUID): Promise<void> {
    return this.request('DELETE', `/v1/me/dependents/${id}`);
  }

  issueDependentCode(id: UUID): Promise<DependentCode> {
    return this.request('POST', `/v1/me/dependents/${id}/code`);
  }

  listDependentReminders(id: UUID): Promise<DependentRemindersResponse> {
    return this.request('GET', `/v1/me/dependents/${id}/reminders`);
  }

  createDependentReminder(id: UUID, input: DependentReminderCreate): Promise<DependentReminder> {
    return this.request('POST', `/v1/me/dependents/${id}/reminders`, { body: input });
  }

  deleteDependentReminder(id: UUID, reminderId: UUID): Promise<void> {
    return this.request('DELETE', `/v1/me/dependents/${id}/reminders/${reminderId}`);
  }

  // ---- Taxonomy ------------------------------------------------------------

  getTaxonomy(profile?: string): Promise<TaxonomyResponse> {
    return this.request('GET', '/v1/taxonomy/areas', { query: { profile } });
  }

  createTaxonomyArea(input: TaxonomyAreaCreate): Promise<TaxonomyArea> {
    return this.request('POST', '/v1/taxonomy/areas', { body: input });
  }

  createTaxonomySubarea(areaSlug: string, input: TaxonomySubareaCreate): Promise<TaxonomyNode> {
    return this.request('POST', `/v1/taxonomy/areas/${encodeURIComponent(areaSlug)}/subareas`, {
      body: input,
    });
  }

  patchTaxonomyNode(slug: string, patch: TaxonomyNodePatch): Promise<TaxonomyNode> {
    return this.request('PATCH', `/v1/taxonomy/areas/${encodeURIComponent(slug)}`, { body: patch });
  }

  deleteTaxonomyNode(slug: string): Promise<void> {
    return this.request('DELETE', `/v1/taxonomy/areas/${encodeURIComponent(slug)}`);
  }

  // ---- Knowledge -----------------------------------------------------------

  listAreas(options?: { profile?: string; userId?: UUID }): Promise<{ areas: KnowledgeArea[] }> {
    return this.request('GET', '/v1/knowledge/areas', {
      query: { profile: options?.profile, user: options?.userId },
    });
  }

  createArea(label: string, parentSlug?: string): Promise<KnowledgeArea> {
    // JSON.stringify drops an absent parentSlug, so a top-level area sends the
    // exact body it always did.
    return this.request('POST', '/v1/knowledge/areas', { body: { label, parentSlug } });
  }

  renameArea(slug: string, label: string): Promise<KnowledgeArea> {
    return this.request('PATCH', `/v1/knowledge/areas/${encodeURIComponent(slug)}`, {
      body: { label },
    });
  }

  deleteArea(slug: string): Promise<void> {
    return this.request('DELETE', `/v1/knowledge/areas/${encodeURIComponent(slug)}`);
  }

  listDocuments(params?: PageParams): Promise<Page<Document>> {
    return this.request('GET', '/v1/documents', { query: paging(params) });
  }

  uploadDocument(file: File, meta: UploadDocumentMeta = {}): Promise<UploadDocumentResult> {
    // Route by size: the API Gateway HTTP API rejects payloads over 10 MB outright,
    // so larger files are PUT straight to S3 via a presigned URL, then ingested.
    return file.size > DIRECT_UPLOAD_MAX_BYTES
      ? this.uploadViaPresigned(file, meta)
      : this.uploadDirect(file, meta);
  }

  /** ≤10 MB: multipart straight through the API Gateway, answered 202 like the
   *  presigned path — a small file can still be a long ingestion. */
  private async uploadDirect(file: File, meta: UploadDocumentMeta): Promise<UploadDocumentResult> {
    const form = new FormData();
    form.append('file', file);
    if (meta.title) form.append('title', meta.title);
    if (meta.source) form.append('source', meta.source);
    if (meta.docType) form.append('docType', meta.docType);
    // The area rides on BOTH upload paths: which one a file takes is decided by
    // its size alone, and a 12 MB spec must land in the same area a 2 MB one does.
    if (meta.area) form.append('area', meta.area);
    if (meta.version) form.append('version', meta.version);
    // Like the area, the destination rides on BOTH upload paths: which one a
    // file takes is decided by its size alone, and a 12 MB file the uploader
    // published must not land private because it was routed differently.
    if (meta.visibility) form.append('visibility', meta.visibility);

    // Multipart: let the browser set Content-Type (incl. the boundary). We still
    // attach the bearer token and normalize failures to ApiError like request().
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = await this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/v1/documents/upload`, {
        method: 'POST',
        headers,
        body: form,
      });
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_error',
        message: 'Não foi possível alcançar o servidor. Verifique sua conexão.',
        detail: String(cause),
      });
    }

    if (!response.ok) throw await toApiError(response, INGEST_SERVER_ERROR_MESSAGE);
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as UploadDocumentResult;
  }

  /**
   * >10 MB: presigned-S3 path in three hops — (1) ask the API for a presigned PUT,
   * (2) PUT the bytes straight to S3 (bypassing the GW 10 MB cap + WAF body scan),
   * (3) ask the API to ingest the object (it deletes it afterwards).
   *
   * Hop 3 answers 202 with a tracking run: downloading, extracting, compressing a
   * heavy PDF and embedding the chunks is minutes to tens of minutes of work the
   * request cannot hold open.
   */
  private async uploadViaPresigned(
    file: File,
    meta: UploadDocumentMeta,
  ): Promise<UploadDocumentResult> {
    // 1) Mint the presigned PUT. ContentType is intentionally NOT sent, so it is
    // not part of the signature and the browser PUT below can't mismatch it.
    const { documentId, key, uploadUrl } = await this.request<{
      documentId: UUID;
      key: string;
      uploadUrl: string;
    }>('POST', '/v1/documents/upload-url', {
      body: { filename: file.name },
      // This hop is part of an UPLOAD as far as the person is concerned, so a
      // bodyless gateway failure here must not read as "Erro inesperado." either —
      // it is the same three-hop operation, and the same tab holds its truth.
      serverErrorMessage: INGEST_SERVER_ERROR_MESSAGE,
    });

    // 2) PUT the file directly to S3. No bearer token — the presigned URL carries
    // its own auth in the query string; the bucket CORS allows this origin.
    let put: Response;
    try {
      put = await fetch(uploadUrl, { method: 'PUT', body: file });
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_error',
        message: 'Não foi possível enviar o arquivo ao armazenamento. Verifique sua conexão.',
        detail: String(cause),
      });
    }
    if (!put.ok) {
      throw new ApiError(put.status, {
        code: 'upload_failed',
        message: 'Falha ao enviar o arquivo para o armazenamento.',
      });
    }

    // 3) Ingest the uploaded object (the API deletes it afterwards).
    return this.request<UploadDocumentResult>('POST', '/v1/documents/ingest-uploaded', {
      body: {
        documentId,
        key,
        title: meta.title,
        source: meta.source,
        docType: meta.docType,
        area: meta.area,
        version: meta.version,
        visibility: meta.visibility,
      },
      serverErrorMessage: INGEST_SERVER_ERROR_MESSAGE,
    });
  }

  /**
   * The download is a two-step on purpose: an `<a href>` cannot carry the bearer
   * token, so the app asks the API for a short-lived signed URL and opens THAT.
   */
  getDocumentDownloadUrl(id: UUID): Promise<{ url: string }> {
    return this.request('GET', `/v1/documents/${encodeURIComponent(id)}/download`);
  }

  /** `null` is sent explicitly, never omitted: the API refuses a body without the
   *  field precisely so a dropped key cannot read as "unfile this document". */
  setDocumentArea(id: UUID, area: string | null): Promise<Document> {
    return this.request('PATCH', `/v1/documents/${encodeURIComponent(id)}`, { body: { area } });
  }

  /** One field per PATCH: the API refuses a body carrying both, because the two
   *  changes answer to different rules and could not be half-applied honestly. */
  setDocumentVisibility(id: UUID, visibility: DocumentVisibility): Promise<Document> {
    return this.request('PATCH', `/v1/documents/${encodeURIComponent(id)}`, {
      body: { visibility },
    });
  }

  /**
   * Enrich an already-indexed document: 202, and the pack is derived from the
   * RETAINED file in the background — the source document is never reindexed.
   * Same wording as the upload paths for a bodyless 5xx: the work may well have
   * continued, and the Ingestão tab holds the truth.
   */
  enrichDocument(id: UUID, input: EnrichDocumentInput): Promise<Training> {
    return this.request('POST', `/v1/documents/${encodeURIComponent(id)}/enrich`, {
      body: input,
      serverErrorMessage: INGEST_SERVER_ERROR_MESSAGE,
    });
  }

  deleteDocument(id: UUID): Promise<void> {
    return this.request('DELETE', `/v1/documents/${encodeURIComponent(id)}`);
  }

  reindex(input: ReindexInput): Promise<IngestionRun> {
    return this.request('POST', '/v1/knowledge/reindex', { body: input });
  }
  listIngestionRuns(params?: PageParams): Promise<IngestionRunsPage> {
    return this.request('GET', '/v1/ingestion-runs', { query: paging(params) });
  }

  // ---- Decks ---------------------------------------------------------------

  listDecks(params?: PageParams): Promise<Page<Deck>> {
    return this.request('GET', '/v1/decks', { query: paging(params) });
  }

  createDeck(input: DeckInput): Promise<Deck> {
    return this.request('POST', '/v1/decks', { body: input });
  }

  updateDeck(id: UUID, input: DeckUpdateInput): Promise<Deck> {
    return this.request('PATCH', `/v1/decks/${id}`, { body: input });
  }

  getDeckDownloadUrl(id: UUID): Promise<{ url: string }> {
    return this.request('GET', `/v1/decks/${id}/download`);
  }

  // ---- Trainings -----------------------------------------------------------

  listTrainings(params?: PageParams): Promise<Page<Training>> {
    return this.request('GET', '/v1/trainings', { query: paging(params) });
  }

  /**
   * Create a training from a theme + source file. Multipart straight through the
   * API Gateway (the endpoint returns 202 with the queued training). We attach the
   * bearer token and normalize failures to ApiError like {@link request}.
   *
   * `visibility`/`area` ride as extra form fields when present: the enrichment
   * is an upload onto a shelf now, not admin-only curation, so the derived pack
   * lands where the caller chose — the API defaults an omitted visibility to
   * the caller's private shelf (an admin's, to the acervo).
   */
  async createTraining(input: TrainingInput): Promise<Training> {
    const form = new FormData();
    form.append('file', input.file);
    form.append('theme', input.theme);
    if (input.visibility) form.append('visibility', input.visibility);
    if (input.area) form.append('area', input.area);

    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = await this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/v1/trainings`, {
        method: 'POST',
        headers,
        body: form,
      });
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_error',
        message: 'Não foi possível alcançar o servidor. Verifique sua conexão.',
        detail: String(cause),
      });
    }

    // Same wording as the document upload: enrichment is also accepted (202) and
    // continued in the background, so a bodyless 5xx here says nothing about
    // whether the training was created — the Ingestão tab does.
    if (!response.ok) throw await toApiError(response, INGEST_SERVER_ERROR_MESSAGE);
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as Training;
  }

  getTraining(id: UUID): Promise<Training> {
    return this.request('GET', `/v1/trainings/${id}`);
  }

  // ---- QA ------------------------------------------------------------------

  ask(input: AskInput): Promise<AskResult> {
    // Sends { question, level } — the level selects answer depth (concisa|completa).
    return this.request('POST', '/v1/knowledge/ask', { body: input });
  }

  /**
   * Async ask: POSTs to /v1/knowledge/asks and gets a 202 receipt — the answer
   * is produced in the background (a complex question runs for minutes, far
   * past the API Gateway's ~30s cut), then followed via {@link getAsk}.
   *
   * The body is the input as given, so an attached `image` (base64 + mimetype)
   * travels with the question and an absent one never reaches the wire.
   */
  askAsync(input: AskAsyncInput): Promise<AskAccepted> {
    return this.request('POST', '/v1/knowledge/asks', { body: input });
  }

  getAsk(askId: UUID): Promise<AskJob> {
    return this.request('GET', `/v1/knowledge/asks/${encodeURIComponent(askId)}`);
  }

  /**
   * The clip travels as the multipart field `audio` with the recorder's own
   * mimetype (codec parameters included): the API picks the decoder from it.
   */
  async transcribeAudio(audio: Blob, mimetype: string): Promise<TranscriptionResult> {
    const form = new FormData();
    form.append('audio', new Blob([audio], { type: mimetype }), 'microfone');

    // Multipart: the browser sets Content-Type with its boundary.
    const headers: Record<string, string> = { Accept: 'application/json' };
    const token = await this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/v1/knowledge/transcribe`, {
        method: 'POST',
        headers,
        body: form,
      });
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_error',
        message: 'Não foi possível alcançar o servidor. Verifique sua conexão.',
        detail: String(cause),
      });
    }

    if (!response.ok) throw await toApiError(response);
    return (await response.json()) as TranscriptionResult;
  }

  // ---- Conversations -------------------------------------------------------

  listConversations(params?: ConversationListParams): Promise<ConversationPage> {
    return this.request('GET', '/v1/conversations', {
      query: {
        surface: params?.surface,
        q: params?.q,
        before: params?.before,
        limit: params?.limit,
      },
    });
  }

  getConversation(id: UUID): Promise<ConversationDetail> {
    return this.request('GET', `/v1/conversations/${encodeURIComponent(id)}`);
  }

  createConversation(input: { surface: AskSurface }): Promise<Conversation> {
    return this.request('POST', '/v1/conversations', { body: input });
  }

  /** O corpo leva só as chaves que o chamador montou: fixar não manda `title`. */
  updateConversation(id: UUID, patch: ConversationPatch): Promise<Conversation> {
    return this.request('PATCH', `/v1/conversations/${encodeURIComponent(id)}`, { body: patch });
  }

  archiveConversation(id: UUID): Promise<void> {
    return this.request('DELETE', `/v1/conversations/${encodeURIComponent(id)}`);
  }

  /**
   * Streaming variant of {@link ask}: POSTs with `stream: true` and yields the
   * SSE events (meta → delta* → done) as they arrive. Unlike {@link request}
   * there is NO overall timeout — the answer trickles in for as long as the
   * model takes; only connect/read failures and non-200 responses throw.
   */
  async *askStream(input: AskInput): AsyncGenerator<AskStreamEvent> {
    const headers: Record<string, string> = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    };
    const token = await this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/v1/knowledge/ask`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...input, stream: true }),
      });
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_error',
        message: 'Não foi possível alcançar o servidor. Verifique sua conexão.',
        detail: String(cause),
      });
    }

    if (!response.ok) throw await toApiError(response);
    if (!response.body) {
      throw new ApiError(0, {
        code: 'network_error',
        message: 'O servidor não abriu o fluxo de resposta. Tente novamente.',
      });
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let match: RegExpMatchArray | null;
        // Frames are separated by a blank line (\n\n or \r\n\r\n).
        while ((match = buffer.match(/\r?\n\r?\n/)) && match.index !== undefined) {
          const frame = buffer.slice(0, match.index);
          buffer = buffer.slice(match.index + match[0].length);
          const event = parseAskStreamFrame(frame);
          if (event) yield event;
        }
      }
      buffer += decoder.decode();
      // Tolerate a final frame missing its blank-line terminator.
      if (buffer.trim()) {
        const event = parseAskStreamFrame(buffer);
        if (event) yield event;
      }
    } catch (cause) {
      if (cause instanceof ApiError) throw cause;
      throw new ApiError(0, {
        code: 'network_error',
        message: 'A conexão caiu durante a resposta. Tente novamente.',
        detail: String(cause),
      });
    } finally {
      // Release the body when the consumer walks away before `done`.
      void reader.cancel().catch(() => undefined);
    }
  }

  listInteractions(params?: PageParams): Promise<Page<QAInteraction>> {
    return this.request('GET', '/v1/knowledge/interactions', { query: paging(params) });
  }

  getInteraction(id: UUID): Promise<QAInteraction> {
    return this.request('GET', `/v1/knowledge/interactions/${id}`);
  }

  sendFeedback(interactionId: UUID, input: FeedbackInput): Promise<FeedbackResult> {
    return this.request('POST', `/v1/knowledge/interactions/${interactionId}/feedback`, {
      body: input,
    });
  }

  // ---- Metrics -------------------------------------------------------------

  getMetricsOverview(): Promise<MetricsOverview> {
    return this.request('GET', '/v1/metrics/overview');
  }

  getMetricsCost(): Promise<MetricsCost> {
    return this.request('GET', '/v1/metrics/cost');
  }

  // ---- Dashboard -----------------------------------------------------------

  getDashboardSummary(): Promise<DashboardSummary> {
    return this.request('GET', '/v1/dashboard/summary');
  }

  // ---- Users ---------------------------------------------------------------

  listUsers(params?: PageParams): Promise<Page<AppUser>> {
    return this.request('GET', '/v1/users', { query: paging(params) });
  }

  createUser(input: UserInput): Promise<AppUser> {
    return this.request('POST', '/v1/users', { body: input });
  }

  updateUser(id: UUID, patch: UserPatch): Promise<AppUser> {
    return this.request('PATCH', `/v1/users/${id}`, { body: patch });
  }

  deleteUser(id: UUID): Promise<void> {
    return this.request('DELETE', `/v1/users/${id}`);
  }

  resendUserInvite(id: UUID): Promise<AppUser> {
    return this.request('POST', `/v1/users/${id}/resend-invite`);
  }

  // ---- AI model settings ---------------------------------------------------

  getAiModelSettings(): Promise<AiModelSettings> {
    return this.request('GET', '/v1/settings/ai-models');
  }

  updateAiModelSettings(patch: AiModelSettingsPatch): Promise<AiModelSettings> {
    return this.request('PUT', '/v1/settings/ai-models', { body: patch });
  }

  // ---- WhatsApp link -------------------------------------------------------

  getWhatsAppLink(): Promise<WhatsAppLink> {
    return this.request('GET', '/v1/me/whatsapp');
  }

  startWhatsAppLink(phone: string): Promise<WhatsAppLinkStarted> {
    return this.request('POST', '/v1/me/whatsapp/link', { body: { phone } });
  }

  verifyWhatsAppLink(code: string): Promise<WhatsAppLink> {
    return this.request('POST', '/v1/me/whatsapp/verify', { body: { code } });
  }

  deleteWhatsAppLink(): Promise<void> {
    return this.request('DELETE', '/v1/me/whatsapp');
  }

  // ---- WhatsApp groups -----------------------------------------------------

  listWhatsAppGroups(): Promise<WhatsAppGroups> {
    return this.request('GET', '/v1/me/whatsapp/groups');
  }

  issueWhatsAppGroupCode(agents: WhatsAppGroupAgentsChoice = 'all'): Promise<WhatsAppGroupCode> {
    return this.request('POST', '/v1/me/whatsapp/groups/code', { body: { agents } });
  }

  listWhatsAppGroupAgentCandidates(): Promise<WhatsAppGroupAgentCandidates> {
    return this.request('GET', '/v1/me/whatsapp/groups/agents');
  }

  setWhatsAppGroupAgents(
    groupId: string,
    agents: WhatsAppGroupAgentsChoice,
  ): Promise<WhatsAppGroup> {
    return this.request(
      'PUT',
      `/v1/me/whatsapp/groups/${encodeURIComponent(groupId)}/agents`,
      { body: { agents } },
    );
  }

  deleteWhatsAppGroup(groupId: string): Promise<void> {
    // Encoded: a group id carries an "@" and dots, and unlike every other id in
    // this client it is a WhatsApp string rather than a UUID we minted.
    return this.request('DELETE', `/v1/me/whatsapp/groups/${encodeURIComponent(groupId)}`);
  }

  // ---- Specialist agents ---------------------------------------------------

  getAgents(profile?: string): Promise<AgentsListing> {
    return this.request('GET', '/v1/agents', { query: { profile } });
  }

  createAgent(input: AgentPersonaCreate): Promise<AgentsResponse> {
    return this.request('POST', '/v1/agents', { body: input });
  }

  updateAgent(id: string, payload: AgentPersonaPut): Promise<AgentsResponse> {
    return this.request('PUT', `/v1/agents/${encodeURIComponent(id)}`, { body: payload });
  }

  deleteAgent(id: string): Promise<void> {
    return this.request('DELETE', `/v1/agents/${encodeURIComponent(id)}`);
  }

  listAgentDocuments(id: string, params: PersonaDocumentsParams = {}): Promise<Page<PersonaDocument>> {
    return this.request('GET', `/v1/agents/${encodeURIComponent(id)}/documents`, {
      query: {
        area: params.area,
        search: params.search,
        linked: params.linked,
        page: params.page,
        page_size: params.pageSize,
      },
    });
  }

  setAgentDocuments(id: string, documentIds: UUID[]): Promise<{ linked: number }> {
    return this.request('PUT', `/v1/agents/${encodeURIComponent(id)}/documents`, {
      body: { documentIds },
    });
  }

  // ---- Personas do próprio leitor ------------------------------------------

  listMyPersonas(): Promise<MyPersonasResponse> {
    return this.request('GET', '/v1/me/personas');
  }

  createMyPersona(input: PersonaUserCreate): Promise<MyPersonaCreated> {
    return this.request('POST', '/v1/me/personas', { body: input });
  }

  updateMyPersona(id: string, payload: PersonaUserPut): Promise<MyPersona> {
    return this.request('PUT', `/v1/me/personas/${encodeURIComponent(id)}`, { body: payload });
  }

  deleteMyPersona(id: string): Promise<void> {
    return this.request('DELETE', `/v1/me/personas/${encodeURIComponent(id)}`);
  }

  listMyPersonaDocuments(
    id: string,
    params: PersonaDocumentsParams = {},
  ): Promise<Page<PersonaDocument>> {
    return this.request('GET', `/v1/me/personas/${encodeURIComponent(id)}/documents`, {
      query: {
        area: params.area,
        search: params.search,
        linked: params.linked,
        page: params.page,
        page_size: params.pageSize,
      },
    });
  }

  setMyPersonaDocuments(id: string, documentIds: UUID[]): Promise<{ linked: number }> {
    return this.request('PUT', `/v1/me/personas/${encodeURIComponent(id)}/documents`, {
      body: { documentIds },
    });
  }

  // ---- Fila de moderação (admin) -------------------------------------------

  listModerationPersonas(params: ModerationListParams = {}): Promise<Page<ModerationItem>> {
    return this.request('GET', '/v1/moderation/personas', {
      query: { status: params.status, page: params.page, page_size: params.pageSize },
    });
  }

  getModerationPersona(id: string): Promise<ModerationItemDetail> {
    return this.request('GET', `/v1/moderation/personas/${encodeURIComponent(id)}`);
  }

  approvePersona(id: string): Promise<ModerationItem> {
    return this.request('POST', `/v1/moderation/personas/${encodeURIComponent(id)}/approve`);
  }

  rejectPersona(id: string, reason: string): Promise<ModerationItem> {
    return this.request('POST', `/v1/moderation/personas/${encodeURIComponent(id)}/reject`, {
      body: { reason },
    });
  }

  // ---- Billing -------------------------------------------------------------

  listBillingPlans(): Promise<BillingPlansResponse> {
    // Público de propósito: a landing precifica os cards antes de qualquer login.
    return this.request('GET', '/v1/billing/plans', { anonymous: true });
  }

  getBillingSubscription(): Promise<BillingSubscription> {
    return this.request('GET', '/v1/billing/subscription');
  }

  listBillingPayments(): Promise<BillingPaymentsResponse> {
    return this.request('GET', '/v1/billing/payments');
  }

  startBillingCheckout(payload: BillingCheckoutInput): Promise<BillingSubscription> {
    return this.request('POST', '/v1/billing/checkout', { body: payload });
  }

  cancelBillingSubscription(): Promise<BillingSubscription> {
    return this.request('POST', '/v1/billing/subscription/cancel');
  }

  getBillingReferrals(): Promise<BillingReferrals> {
    return this.request('GET', '/v1/billing/referrals');
  }

  attachBillingReferral(code: string): Promise<BillingReferrals> {
    return this.request('POST', '/v1/billing/referrals/attach', { body: { code } });
  }

  // ---- Widget público do site ----------------------------------------------

  siteChat(input: SiteChatInput): Promise<SiteChatReply> {
    return this.request('POST', '/public/site-chat', { body: input, anonymous: true });
  }

  /**
   * The {@link transcribeAudio} multipart (`audio` with the recorder's mimetype)
   * plus the widget's `sessionId` field, which the per-session limit counts — and
   * anonymous: the session token, if there is one, never rides along.
   */
  async transcribeSiteAudio(audio: Blob, mimetype: string, sessionId: string): Promise<TranscriptionResult> {
    const form = new FormData();
    form.append('audio', new Blob([audio], { type: mimetype }), 'microfone');
    form.append('sessionId', sessionId);

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/public/site-chat/transcribe`, {
        method: 'POST',
        // Multipart: the browser sets Content-Type with its boundary.
        headers: { Accept: 'application/json' },
        body: form,
      });
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_error',
        message: 'Não foi possível alcançar o servidor. Verifique sua conexão.',
        detail: String(cause),
      });
    }

    if (!response.ok) throw await toApiError(response);
    return (await response.json()) as TranscriptionResult;
  }

  // ---- Transport -----------------------------------------------------------

  private async request<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
    const url = this.baseUrl + path + serializeQuery(opts.query);
    const headers: Record<string, string> = { Accept: 'application/json' };

    const token = opts.anonymous ? null : await this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const multipart = opts.body instanceof FormData;
    if (opts.body !== undefined && !multipart) headers['Content-Type'] = 'application/json';
    const body: BodyInit | undefined = multipart
      ? (opts.body as FormData)
      : opts.body !== undefined
        ? JSON.stringify(opts.body)
        : undefined;

    let response: Response;
    try {
      response = await fetch(url, { method, headers, body });
    } catch (cause) {
      throw new ApiError(0, {
        code: 'network_error',
        message: 'Não foi possível alcançar o servidor. Verifique sua conexão.',
        detail: String(cause),
      });
    }

    if (!response.ok) {
      throw await toApiError(response, opts.serverErrorMessage);
    }

    if (response.status === 204) return undefined as T;

    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }
}

function paging(params?: PageParams): Query {
  if (!params) return {};
  return {
    page: params.page,
    page_size: params.pageSize,
    search: params.search,
    sort: params.sort,
    order: params.order,
    area: params.area,
    scope: params.scope,
  };
}

function serializeQuery(query?: Query): string {
  if (!query) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Parse one SSE frame ("event:"/"data:" lines) into an {@link AskStreamEvent}.
 * Unknown event types return null (forward-compatible); comment/heartbeat
 * lines are ignored. A malformed JSON payload for a KNOWN event throws — a
 * corrupt answer must surface as a retryable error, never silently drop text.
 */
function parseAskStreamFrame(frame: string): AskStreamEvent | null {
  let event = '';
  const data: string[] = [];
  for (const rawLine of frame.split(/\r?\n/)) {
    const line = rawLine.endsWith('\r') ? rawLine.slice(0, -1) : rawLine;
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      event = line.startsWith('event: ') ? line.slice(7) : line.slice(6);
    } else if (line.startsWith('data:')) {
      data.push(line.startsWith('data: ') ? line.slice(6) : line.slice(5));
    }
  }
  if (!event || data.length === 0) return null;

  const payload = JSON.parse(data.join('\n')) as Record<string, unknown>;
  switch (event) {
    case 'meta':
      return {
        type: 'meta',
        citations: (payload.citations ?? []) as Citation[],
        confidence: typeof payload.confidence === 'number' ? payload.confidence : 0,
        usedContext: payload.usedContext === true,
      };
    case 'delta':
      return { type: 'delta', text: typeof payload.text === 'string' ? payload.text : '' };
    case 'done':
      return {
        type: 'done',
        interactionId: String(payload.interactionId ?? ''),
        truncated: payload.truncated === true,
      };
    default:
      return null;
  }
}

/**
 * Normalize a failed response to an {@link ApiError}.
 *
 * A 5xx on an endpoint that supplied `serverErrorMessage` always gets that
 * wording, body or no body: the API Gateway's timeout answers a perfectly valid
 * JSON envelope of its own (`{"message":"Endpoint request timed out"}`), which is
 * true and useless to the person who just uploaded a file — so it rides along in
 * `detail` while the message says what to actually do. Without such a hint, a
 * bodyless failure falls back to `statusText`, which HTTP/2 leaves empty.
 *
 * A code whose wording the console owns (see {@link messageForCode}) always gets
 * that wording, whatever text the server sent beside it.
 */
async function toApiError(response: Response, serverErrorMessage?: string): Promise<ApiError> {
  let body: Record<string, unknown> | null = null;
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    body = null;
  }

  if (response.status >= 500 && serverErrorMessage) {
    return new ApiError(response.status, {
      code: typeof body?.code === 'string' ? body.code : `http_${response.status}`,
      message: serverErrorMessage,
      detail: body ?? undefined,
    });
  }

  if (body === null) {
    return new ApiError(response.status, {
      code: `http_${response.status}`,
      message: response.statusText || 'Erro inesperado.',
    });
  }

  const code = envelopeCode(body, response.status);
  return new ApiError(response.status, {
    code,
    message:
      messageForCode(code) ??
      (typeof body.message === 'string'
        ? body.message
        : typeof body.detail === 'string'
          ? body.detail
          : response.statusText),
    detail: body.detail,
  });
}

/**
 * O código de erro do corpo de uma falha: o `code` plano do envelope da API ou,
 * se o corpo o traz aninhado como `{detail: {code}}`, esse — assim o código
 * sobrevive às duas formas com que a borda pode responder. Sem nenhum dos dois,
 * o genérico `http_<status>`, que o mock também usa.
 */
function envelopeCode(body: Record<string, unknown>, status: number): string {
  if (typeof body.code === 'string') return body.code;
  const { detail } = body;
  if (typeof detail === 'object' && detail !== null && 'code' in detail && typeof detail.code === 'string') {
    return detail.code;
  }
  return `http_${status}`;
}
